import { Box, FormHelperText, InputLabel, Stack, TextFieldProps } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ptBR } from 'date-fns/locale';
import * as React from 'react';
import { container, datePickerStyle, helperStyle, labelStyle } from './styles';
import { InputProps } from './types';

export default function InputDateComponent({
  appId,
  name,
  label,
  helperText,
  isDirty: _isDirty,
  setField,
  defaultValue,
  ...props
}: InputProps & TextFieldProps) {
  const [value, setValue] = React.useState<Date | null>(null);

  const parseStringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    // Se é uma string no formato DD/MM/YYYY
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return isValidDate(date) ? date : null;
    }

    // Se é uma string ISO
    const date = new Date(dateString);
    return isValidDate(date) ? date : null;
  };

  const isValidDate = (date: Date | null) => {
    if (date) {
      return !isNaN(date.getDate());
    } else {
      return false;
    }
  };

  const handleChange = (newValue: Date | null) => {
    setValue(newValue);

    if (setField) {
      setField(isValidDate(newValue) && newValue ? newValue?.toISOString() : '');
    }
  };

  React.useEffect(() => {
    // Prioritize defaultValue, then props.value
    if (defaultValue) {
      setValue(defaultValue);
    } else if (props.value && typeof props.value === 'string') {
      const parsedDate = parseStringToDate(props.value);
      setValue(parsedDate);
    } else {
      setValue(null);
    }
  }, [defaultValue, props.value]);

  const fieldId = appId ?? `input-${name}`;

  return (
    <Box data-app-id={appId ? `${appId}-control` : undefined} sx={container}>
      <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
        <InputLabel data-app-id={appId ? `${appId}-label` : undefined} htmlFor={fieldId} shrink sx={labelStyle}>
          {label}
        </InputLabel>
        <Stack data-app-id={appId ? `${appId}-input` : undefined}>
          <DesktopDatePicker
            disabled={props.disabled}
            disableFuture
            format="dd/MM/yyyy"
            onChange={handleChange}
            slotProps={{
              textField: {
                id: fieldId,
                sx: datePickerStyle,
                inputProps: appId ? { 'data-app-id': `${appId}-input-element` } : undefined,
                InputProps: { notched: false }
              }
            }}
            value={value}
          />
          {helperText && props.error && (
            <>
              <FormHelperText sx={helperStyle}>{helperText}</FormHelperText>
            </>
          )}
        </Stack>
      </LocalizationProvider>
    </Box>
  );
}
